export { determineEventMode, type EventMode, type EventWindows } from "./event-mode";

import { readConventionWindow } from "./convention-clock";

export {
  ROOM_RECORDER_GRACE_MINUTES,
  instantOfWallClock,
  positionInWindows,
  readConventionWindow,
  readInstantWindow,
  wallClockIn,
  windowsOverlap,
  type ConventionWindow,
  type ConventionWindowReading,
  type ConventionWindowState,
  type InstantWindow,
} from "./convention-clock";

export {
  deriveSchoolConventionLifecycle,
  type SchoolConventionActivityInput,
  type SchoolConventionAttentionCode,
  type SchoolConventionAttentionItem,
  type SchoolConventionBlockCoverage,
  type SchoolConventionBlockInput,
  type SchoolConventionProposalInput,
  type SchoolConventionFacultyInput,
  type SchoolConventionLifecycleInput,
  type SchoolConventionLifecycleResult,
  type SchoolConventionMode,
  type SchoolConventionParticipationIntent,
  type SchoolConventionPlanState,
  type SchoolConventionPriorityInput,
} from "./school-convention-lifecycle";

export {
  MOBILE_EXPERIENCE_KINDS,
  MOBILE_ICON_KEYS,
  resolveMobileExperience,
  type MobileExperienceCandidate,
  type MobileExperienceDecision,
  type MobileExperienceEventInput,
  type MobileExperienceInput,
  type MobileExperienceKind,
  type MobileExperienceOption,
  type MobileExperiencePreference,
  type MobileExperienceSelectionReason,
  type MobileIconKey,
  type MobileNavigationDestination,
  type MobileProgramKind,
} from "./mobile-experience";

export interface ConventionBlockWindow {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
}

export interface ConventionPosition {
  phase: "before" | "during" | "between" | "after";
  currentBlockId: string | null;
  nextBlockId: string | null;
}

/**
 * Where the convention has got to: what is running, and what is next.
 *
 * Asked by the schedule every attendee opens, by the organizer's live screen, by both
 * mobile overviews and by the card on the lock screen. It reads the convention clock and
 * does no time arithmetic of its own.
 */
export function findConventionPosition(
  blocks: readonly ConventionBlockWindow[],
  timeZone: string,
  now: Date,
): ConventionPosition {
  if (blocks.length === 0) {
    return { phase: "after", currentBlockId: null, nextBlockId: null };
  }
  const at = now.getTime();
  const ordered = blocks
    .map((block) => {
      const reading = readConventionWindow(block, now, timeZone);
      return { id: block.id, starts: Date.parse(reading.startsAt), ends: Date.parse(reading.endsAt) };
    })
    .sort((left, right) => left.starts - right.starts);

  // The end is exclusive because the blocks abut. Seminar 1 finishes at 12:15 and lunch
  // begins at 12:15, and at that minute a teacher is at lunch rather than in the seminar.
  const current = ordered.find((block) => block.starts <= at && at < block.ends);
  const next = ordered.find((block) => block.starts > at);
  if (current) {
    return { phase: "during", currentBlockId: current.id, nextBlockId: next?.id ?? null };
  }
  if (next) {
    return {
      phase: at < ordered[0]!.starts ? "before" : "between",
      currentBlockId: null,
      nextBlockId: next.id,
    };
  }
  return { phase: "after", currentBlockId: null, nextBlockId: null };
}

export interface SearchableScheduleItem {
  id: string;
  dayId: string;
  kind: string;
  roomId: string | null;
  audienceIds: readonly string[];
  title: string;
  description: string | null;
  presenterNames: readonly string[];
  roomText: string | null;
  audienceLabels: readonly string[];
}

export interface ScheduleFilters {
  query?: string;
  dayId?: string;
  kind?: string;
  roomId?: string;
  audienceId?: string;
}

export function filterSchedule(
  items: readonly SearchableScheduleItem[],
  filters: ScheduleFilters,
): readonly SearchableScheduleItem[] {
  const query = normalize(filters.query ?? "");
  return items.filter((item) => {
    if (filters.dayId && item.dayId !== filters.dayId) return false;
    if (filters.kind && item.kind !== filters.kind) return false;
    if (filters.roomId && item.roomId !== filters.roomId) return false;
    if (filters.audienceId && !item.audienceIds.includes(filters.audienceId)) return false;
    if (!query) return true;
    return [
      item.title,
      item.description,
      item.roomText,
      ...item.presenterNames,
      ...item.audienceLabels,
    ].some((value) => value !== null && normalize(value).includes(query));
  });
}

function normalize(value: string): string {
  return value.normalize("NFKD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("en-US").trim();
}

export {
  buildDeterministicLearningDraft,
  buildLearningEvidence,
  sessionKnowledgeStatements,
  withSessionKnowledge,
  type LearningDraftValues,
  type LearningEvidenceInput,
  type LearningEvidenceKind,
  type LearningEvidencePackage,
  type LearningEvidenceSource,
} from "./learning-evidence";

export {
  buildConferenceReviewEvidence,
  buildDeterministicConferenceReview,
  type ConferenceReviewEntry,
  type ConferenceReviewEvidencePackage,
  type ConferenceReviewEvidenceRecord,
  type ConferenceReviewValues,
} from "./conference-review";

export { simplifyTeacherLanguage } from "./learning-language";

export {
  AUTHORITY_PERMISSIONS,
  actorHasPermission,
  authorityScopesMatch,
  permissionsForAuthorityRole,
  type AuthorityGrant,
  type AuthorityPermission,
  type AuthorityRole,
  type AuthorityScope,
  type RequestActor,
} from "./authority";

export {
  sessionKnowledgeDraftAllowed,
  sessionKnowledgeFingerprint,
  validateSessionKnowledgeValues,
  type SessionKnowledgeEvidencePackage,
  type SessionKnowledgeRecord,
  type SessionKnowledgeSource,
  type SessionKnowledgeSourceKind,
  type SessionKnowledgeStatement,
  type SessionKnowledgeValues,
  type SessionKnowledgeVoice,
} from "./session-knowledge";

export {
  CONTACT_HOURS_PER_SESSION,
  MAX_CONTACT_HOURS_PER_CONVENTION,
  WORKSHOP_CATEGORIES,
  earnsContactHours,
  proposedCategory,
  voucherTotal,
  type CreditableKind,
  type VoucherLine,
  type VoucherTotal,
  type WorkshopCategory,
  type WorkshopCategoryNumber,
} from "./continuing-education";

export {
  PRESENCE_CODE_ALPHABET,
  PRESENCE_CODE_LENGTH,
  formatPresenceCode,
  normalizePresenceCode,
  presenceCodeFrom,
  presenceLinkFor,
} from "./activity-presence";

export {
  VOUCHER_FIELD,
  VOUCHER_FIELD_RULES,
  VOUCHER_PAGE_HEIGHT,
  VOUCHER_RULES_FROM_TOP,
  VOUCHER_TABLE,
  VOUCHER_TITLE_MAX_WIDTH,
  formatConventionDates,
  voucherFileName,
  voucherRowFromTop,
} from "./voucher-form-geometry";

export {
  ASSOCIATION_ROLES,
  ORGANIZER_PERMISSIONS,
  ORGANIZER_ROLES,
  hasOrganizerPermission,
  permissionsForAssociationRole,
  permissionsForRoles,
  type AssociationRole,
  type OrganizerPermission,
  type OrganizerRole,
} from "./authorization";

export {
  RoomChangeError,
  validateRoomChange,
  type RoomChangeErrorCode,
} from "./schedule-room-change";

export {
  ScheduleContentChangeError,
  validateScheduleContentChange,
  type ScheduleContentChangeErrorCode,
  type ScheduleItemContentValues,
} from "./schedule-content-change";

export {
  SCHEDULE_ITEM_KIND_LABELS,
  SCHEDULE_ITEM_KINDS,
  ScheduleKindChangeError,
  isScheduleItemKind,
  validateScheduleKindChange,
  type ScheduleItemKind,
  type ScheduleKindChangeErrorCode,
} from "./schedule-kind-change";
export {
  RECORDING_PERMISSION_EXPLANATION,
  RECORDING_PERMISSION_LABEL,
  RECORDING_PERMISSION_ROLES,
  RECORDING_PERMISSION_STATES,
  RecordingPermissionError,
  isRecordingPermissionState,
  overrideValueForState,
  resolveRecordingPermission,
  stateForOverrideValue,
  validateRecordingDefaultChange,
  validateRecordingPermissionChange,
  type RecordingPermissionErrorCode,
  type RecordingPermissionRole,
  type RecordingPermissionState,
} from "./session-recording";
export {
  roomRecorderState,
  roomRecordingShouldStop,
  type RoomRecorderState,
  type RoomSession,
} from "./room-recording";
export {
  ScheduleStructureChangeError,
  validateScheduleStructureChange,
  type ScheduleStructureAction,
  type ScheduleStructureValues,
} from "./schedule-structure-change";

export {
  EVENT_ASSET_ACCEPT,
  EVENT_ASSET_EXTENSIONS,
  EVENT_ASSET_MAX_BYTES,
  EVENT_ASSET_MEDIA_TYPES,
  EventAssetValidationError,
  validateEventAsset,
  type EventAssetDescriptor,
  type EventAssetMediaType,
  type EventAssetValidationErrorCode,
} from "./event-asset";

export {
  EventConfigurationChangeError,
  validateEventDisplayChange,
  type EventConfigurationChangeErrorCode,
  type EventDisplayValues,
} from "./event-configuration-change";

export {
  ScheduleBlockChangeError,
  validateScheduleBlockChange,
  type ScheduleBlockChangeErrorCode,
  type ScheduleBlockValues,
  type ScheduleBlockWindow,
} from "./schedule-block-change";

export {
  PresenterRecordChangeError,
  validatePresenterRecordChange,
  type PresenterRecordChangeErrorCode,
  type PresenterRecordValues,
} from "./presenter-record-change";

export {
  PublicUpdateError,
  validatePublicUpdate,
  type PublicUpdateErrorCode,
  type PublicUpdateValues,
} from "./public-update";

export {
  normalizeRegistrationEmail,
  parseCsv,
  validateRegistrationEntry,
  validateRegistrationImport,
  RegistrationEntryError,
  type RegistrationImportFinding,
  type RegistrationImportMapping,
  type RegistrationImportValidation,
  type RegistrationEntry,
  type RegistrationEntryErrorCode,
  type ValidatedRegistrationEntry,
  type ValidatedRegistrationRow,
} from "./attendee-registration";

export { PASSWORD_MAXIMUM_LENGTH, PASSWORD_MINIMUM_LENGTH } from "./password-rules";

export {
  SelfRegistrationEntryError,
  decideSelfRegistration,
  lookupKeyForTypedEmail,
  type ExistingRegistration,
  type SelfRegistrationInput,
  type SelfRegistrationOutcome,
} from "./self-registration";

export {
  buildSignInEmail,
  signInLinkUrl,
  SignInEmailError,
  type SignInAudience,
  type SignInEmailContent,
  type SignInEmailInput,
} from "./sign-in-email";

export {
  SIGN_IN_DOORS,
  deviceLinkPagePath,
  doorAccessName,
  doorBasePath,
  isSignInDoor,
  signInPagePath,
  type SignInDoor,
} from "./sign-in-doors";

export {
  SIGN_IN_REFUSALS,
  SignInRefusalError,
  assertRefusalMayBeShown,
  describeSignInRefusal,
  revealsWhoHasAccess,
  type SignInRefusal,
  type SignInRefusalContext,
  type SignInRefusalReason,
} from "./sign-in-refusal";

export {
  NO_SCHOOL_KEY,
  matchSuppliedSchools,
  needsConfirmation,
  resolveRowOrganizations,
  suppliedSchoolKey,
  summariseSuppliedSchools,
  type SchoolMatchResult,
  type SuppliedSchool,
} from "./school-matching";

export {
  MEMBERSHIPS,
  MEMBERSHIP_TYPES,
  MemberOrganizationError,
  ORGANIZATION_TYPES,
  accreditingBody,
  describeMembership,
  isCurrentMember,
  isMembership,
  isMembershipType,
  isOrganizationType,
  matchOrganization,
  normalizeOrganizationCode,
  normalizeOrganizationName,
  validateMemberOrganization,
  validateMembership,
  type MemberOrganizationInput,
  type Membership,
  type MembershipType,
  type OrganizationCandidate,
  type OrganizationMatch,
  type OrganizationType,
  type ValidatedMemberOrganization,
} from "./member-organization";

export {
  EMPTY_TALLY,
  REGISTRATION_CATEGORIES,
  REGISTRATION_SOURCE_BY_HAND,
  REGISTRATION_SOURCE_ORGANIZER,
  REGISTRATION_SOURCE_PRESENTER,
  awaitingSchool,
  describeTally,
  isAccessRegistration,
  registrationCategory,
  registrationSourceForDoor,
  schoolPlacement,
  tallyRegistrations,
  type RegistrationCategory,
  type RegistrationTally,
  type SchoolPlacement,
} from "./registration-access";

export {
  PRESENTER_RESOURCE_MAX_BYTES,
  PRESENTER_RESOURCE_MEDIA_TYPES,
  PRESENTER_RESOURCE_SLOT_GUIDE,
  PRESENTER_RESOURCE_SLOTS,
  PresenterResourceError,
  presenterResourceIsOpen,
  validatePresenterResource,
  type PresenterResourceErrorCode,
  type PresenterResourceKind,
  type PresenterResourceSlot,
  type PresenterResourceSessionStart,
  type PresenterResourceSlotGuide,
  type PresenterResourceState,
  type PresenterResourceSubmission,
  type ValidatedPresenterResource,
} from "./presenter-resource";
export {
  ROLE_SUMMARIES,
  checkInvitationIsNew,
  checkRemovalIsSafe,
  describeRole,
  isOrganizerRole,
  normalizeOrganizerEmail,
  OrganizerInvitationError,
  validateAccessIdentity,
  validateOrganizerInvitation,
  type ExistingOrganizer,
  type OrganizerInvitationInput,
  type ValidatedAccessIdentity,
  type ValidatedOrganizerInvitation,
} from "./organizer-invitation";
export {
  SESSION_COOKIE_MAX_AGE_SECONDS,
  SESSION_LIFETIME_MS,
  SESSION_RENEW_AFTER_MS,
  sessionRenewal,
} from "./session-life";
export {
  MAX_PASSKEYS_PER_PERSON,
  PASSKEY_CHALLENGE_LIFETIME_MS,
  PASSKEY_LABEL_MAX_LENGTH,
  PasskeyError,
  checkPasskeyAllowance,
  checkPasskeyChallengeUsable,
  checkPasskeyCounter,
  passkeyChallengeExpiry,
  passkeyLabel,
  type PasskeyChallengeRecord,
  type PasskeyErrorCode,
  type PasskeyPurpose,
} from "./passkey";
export {
  isRuntimeSettingKey,
  isValidRuntimeSettingValue,
  plainMoment,
  runtimeSettingApplies,
  runtimeSettingDefinition,
  runtimeSettingDefinitions,
  runtimeSettingSaid,
  type RuntimeSettingDefinition,
  type RuntimeSettingKey,
  type RuntimeSettingSaid,
  type RuntimeSettingShape,
} from "./runtime-setting";
export {
  MARKER_LABELS,
  noteExportLines,
  noteExportText,
  type ExportedNote,
  type ExportLine,
  type NoteExport,
} from "./note-export";

export {
  EMPTY_NOTE_DOCUMENT,
  MAX_LIST_DEPTH,
  noteDocumentFromText,
  noteDocumentText,
  sanitizeNoteDocument,
  type NoteBlock,
  type NoteDocument,
  type NoteListItem,
  type NoteMark,
  type NoteTaskItem,
  type NoteText,
} from "./note-document";

export {
  COVERAGE_TIE_SECONDS,
  MINIMUM_COVERAGE_FRACTION,
  chooseOfficialRecording,
  type RecordingCandidate,
  type RecordingChoice,
} from "./recording-choice";

export {
  PLATFORM_INSTALL_IDENTITY,
  NEWEST_SHELL_GENERATION,
  SHELL_MEMORY_COOKIE,
  SHELL_MEMORY_MAX_AGE_SECONDS,
  SHELL_USER_AGENT_TOKEN,
  associationInstallIdentity,
  phoneBrowserForUserAgent,
  rememberedShell,
  rememberedShellValue,
  shellGenerationForUserAgent,
  shellIsBehind,
  shellPlatformForUserAgent,
  webManifest,
  type AssociationInstallFields,
  type InstallIdentity,
  type ManifestIcon,
  type RememberedShell,
  type ShellPlatform,
  type WebManifest,
} from "./install-identity";

export {
  MAX_MAIL_NAME_LENGTH,
  MAX_SIGN_OFF_LENGTH,
  MAX_SIGN_OFF_LINES,
  PLATFORM_MAIL_IDENTITY,
  associationMailIdentity,
  checkMailSettings,
  checkSignOff,
  mailSignOffLines,
  mailboxDomain,
  mailboxName,
  sendableMailDomains,
  type AssociationMailFields,
  type MailIdentity,
  type MailSettingsInput,
  type MailSettingsRefusal,
  type SignOffRefusal,
} from "./mail-identity";
export {
  STAFF_DOMAIN,
  STAFF_DOMAINS,
  STAFF_NOT_ADMINISTRATOR_MESSAGE,
  judgeStaffSignIn,
  type GoogleIdentityClaims,
  type StaffSignInJudgement,
  type StaffSignInRefusal,
} from "./staff-sign-in";
export {
  HUMAN_CHECK_BROKEN_REFUSALS,
  HUMAN_CHECK_BROKEN_SOURCES,
  HUMAN_CHECK_WINDOW_MS,
  describeHumanCheckStandDown,
  judgeHumanCheck,
  type HumanCheckCounts,
  type HumanCheckVerdict,
} from "./human-check";
export {
  DOOR_BROKEN_SHARE,
  DOOR_MINIMUM_CODES,
  DOOR_STRUGGLING_SHARE,
  DOOR_WINDOW_MS,
  doorRemedies,
  readDoorHealth,
  type DoorCounts,
  type DoorHealth,
} from "./door-health";
export {
  CHECK_IN_LINE,
  CHOOSE_MINUTES,
  WALK_MINUTES,
  buildLiveCard,
  instantOf,
  type LiveCard,
  type LiveCardBlock,
  type AwaitingFeedback,
  type LiveCardInput,
  type LiveCardItem,
  type LiveCardState,
  type LiveCardTone,
} from "./live-card";
export {
  DISMISS_AFTER_MS,
  STALE_GRACE_MS,
  decideLiveCard,
  sameCard,
  type LiveCardAction,
  type LiveCardDecision,
  type LiveCardDeliveryState,
} from "./live-card-delivery";
export {
  LIVE_CARD_EVERYONE,
  liveCardAllowed,
  readLiveCardAudience,
  type LiveCardAudience,
  type LiveCardAudienceMode,
} from "./live-card-audience";

export { expandedTitleWorthShowing, sameWords } from "./expanded-title";
export {
  presenterAllowsRecording,
  sessionRecordingAllowed,
  type PresenterRecordingPermission,
  type SessionRecordingAnswer,
  type SessionRecordingQuestion,
} from "./presenter-recording-permission";
export {
  AFTER_LAST_SESSION_MINUTES,
  EARLIEST_AFTER_SESSION_MINUTES,
  LEAD_BEFORE_NEXT_SESSION_MINUTES,
  SETTLE_INTO_BREAK_MINUTES,
  SURVEY_ALERT_MOMENTS,
  blockIsSomewhereToBe,
  surveyAlertMoments,
  surveyPromptMoment,
  type PromptScheduleBlock,
} from "./survey-prompt-schedule";
export {
  LONGEST_TERM,
  SHORTEST_TERM,
  normalizeSearchTerm,
  supersedesSearchTerm,
} from "./search-term";
export {
  ENOUGH_TO_READ,
  rankByRevealedValue,
  rotationCoverage,
  sessionFindings,
  warmth,
  writeUpRate,
  type SessionFinding,
  type SessionFindingKind,
  type SessionSignals,
} from "./session-signals";
export {
  SESSION_STANDINGS,
  STUDIED_EXPLANATION,
  STUDIED_LABEL,
  mayBeListedForCredit,
  standingFrom,
  type SessionStanding,
} from "./session-standing";
export {
  RECORDING_GRACE_SECONDS,
  trimRecordingToSession,
  type RecordingSegment,
  type RecordingWindow,
  type TrimmedRecording,
} from "./recording-window";
export {
  SURVEY_OPENS_RULES,
  SURVEY_OPENS_RULE_LABELS,
  closesRuleFor,
  daysAfterEvent,
  resolveClosesAt,
  resolveOpensAt,
  type SurveyOpensRule,
  type SurveyTimingBlock,
} from "./survey-timing";
export {
  SURVEY_READING_CAUTION_BELOW,
  buildSurveyReadingEvidence,
  keepOnlyGroundedThemes,
  readingCaution,
  readingWithheld,
  type SurveyReadingAnswer,
  type SurveyReadingEvidence,
  type SurveyReadingQuote,
  type SurveyReadingResponse,
  type SurveyReadingScope,
  type SurveyReadingTheme,
  type SurveyReadingValues,
} from "./survey-reading";
export {
  ROTATION_VERSION,
  rotateQuestions,
  rotationHash,
  rotationIndex,
  rotationSeed,
  rotationSpread,
  surveyCardShape,
  surveySlotNumber,
  type SurveyCardShape,
  type SurveyCardSlot,
} from "./survey-rotation";
export {
  FEEDBACK_HELD,
  canRelease,
  presenterFeedbackVisibility,
  type FeedbackVisibility,
  type SessionFeedbackRelease,
} from "./session-feedback-release";
export {
  planAccountDeletion,
  type AccountDeletionContext,
  type AccountDeletionPlan,
  type AccountDeletionRemoval,
  type AccountDeletionRetention,
} from "./account-deletion";
export {
  CLIENT_FAULT_KINDS,
  FAULT_WINDOW_MS,
  SIGNAL_WINDOW_MS,
  describeFault,
  faultRule,
  isClientFaultKind,
  raisedFaults,
  worstSeverity,
  type AlertSeverity,
  type ClientFaultKind,
  type FaultCount,
  type FaultRule,
  type RaisedFault,
} from "./client-fault";
export {
  MAX_CODES_PER_PERSON,
  MAX_CODES_PER_SOURCE,
  MAX_SQUARES_PER_PERSON,
  MAX_UNKNOWN_ADDRESSES_PER_SOURCE,
  MAX_REFUSED_JOINS_PER_SOURCE,
  MAX_REFUSED_JOINS_PER_PERSON,
  MAX_JOINS_PER_SOURCE,
  SIGN_IN_WINDOW_MINUTES,
  SIGN_IN_WINDOW_MS,
  signInAllowance,
  signInAllowanceSpent,
  signInRetryAfterMinutes,
  type SignInAllowance,
  type SignInTally,
} from "./sign-in-allowance";
export {
  DEVICE_LINK_LIFETIME_MS,
  DEVICE_LINK_MAX_PER_WINDOW,
  DEVICE_LINK_WINDOW_MS,
  DeviceLinkError,
  checkDeviceLinkAllowance,
  checkDeviceLinkUsable,
  deviceLinkExpiry,
  deviceLinkRefusal,
  deviceLinkUrl,
  type DeviceLinkErrorCode,
  type DeviceLinkRecord,
} from "./device-link";

export {
  CLAIM_CODE_GROUP,
  CLAIM_CODE_LENGTH,
  claimCodeLookup,
  claimUrl,
  deriveClaimCode,
  formatClaimCode,
  generateClaimCode,
  isWellFormedClaimCode,
  normalizeClaimCode,
} from "./claim-code";
export {
  PRESENTER_HEADSHOT_LONG_EDGE,
  PRESENTER_HEADSHOT_MAX_BYTES,
  PRESENTER_HEADSHOT_MEDIA_TYPES,
  PresenterHeadshotError,
  presenterHeadshotTargetSize,
  validatePresenterHeadshot,
} from "./presenter-headshot";
export {
  PRESENTER_WELCOME_LOCAL_PART_MAX,
  presenterWelcomeReplyTo,
  presenterWelcomeSenderAddress,
  PRESENTER_WELCOME_BODY,
  PRESENTER_WELCOME_SUBJECT,
  PresenterWelcomeEmailError,
  buildPresenterWelcomeEmail,
  validatePresenterWelcomeTemplate,
} from "./presenter-welcome-email";
export {
  ATTENDEE_INVITATION_BODY,
  ATTENDEE_INVITATION_SUBJECT,
  AttendeeInvitationEmailError,
  buildAttendeeInvitationEmail,
  validateAttendeeInvitationTemplate,
} from "./attendee-invitation-email";
export {
  SURVEY_EMAIL_BODY_LIMIT,
  SURVEY_EMAIL_SUBJECT_LIMIT,
  SURVEY_OPEN_FOR_DAYS,
  defaultSurveyClosesAt,
  SurveyEmailError,
  buildSurveyEmail,
  resolveSurveyEmailSendAt,
  surveyEmailUrl,
  validateSurveyEmailTemplate,
} from "./survey-email";
export type {
  SurveyEmailContent,
  SurveyEmailInput,
  SurveyEmailSendMode,
  SurveyEmailTemplate,
} from "./survey-email";
export {
  EventLocationError,
  validateEventAddress,
  validateEventLocation,
  type EventAddressValues,
  type EventLocationValues,
} from "./event-location";
export {
  agentInvestigationProposalDocument,
  canTransitionMaintenanceCase,
  decisionIsUsable,
  isCapabilityId,
  requireMaintenanceCaseTransition,
  SIGN_IN_CODE_REMEDY_DURATION_MS,
  SIGN_IN_CODE_REMEDY_ID,
  SIGN_IN_CODE_REMEDY_MINUTES,
  SIGN_IN_HUMAN_CHECK_REMEDY_DURATION_MS,
  SIGN_IN_HUMAN_CHECK_REMEDY_ID,
  SIGN_IN_SOURCE_REMEDY_DURATION_MS,
  SIGN_IN_SOURCE_REMEDY_ID,
  signInRemedyExpiryIsAllowed,
  signInRemedyProposalDocument,
  signInRemedySetting,
  supportProfileForCapability,
  operationsScopeFromIds,
  type CapabilitySupportProfile,
  scopesMatch,
  type DecisionValidityInput,
  type MaintenanceCaseState,
  type MaintenanceDecision,
  type OperationsCoverage,
  type OperationsScope,
  type OperationsScopeIds,
  type TenantOperationsScope,
  type OperationsState as MaintenanceOperationsState,
  type SignInRemedyId,
  type SignInRemedySetting,
} from "./operations";
export {
  servicePacketForCapability,
  servicePackets,
  type DetectorCoverage,
  type ServicePacket,
  type ServicePacketDeliveryState,
  type ServicePacketState,
  type ServicePathState,
} from "./service-packet";

/*
 * Everything that watches this product.
 *
 * Kept behind a name rather than spread across this file, for two reasons. The sensing
 * layer grows a detector at a time and each one would otherwise be a line here and a merge
 * conflict for whoever else is editing it. And `reading`, `journey` and `coverage` are
 * words this package already uses for other things — `detectors.reading()` says which is
 * meant, where a bare export would not.
 */
export * as detectors from "./detectors";
export {
  DELIVERY_GRACE_MS,
  DELIVERY_MINIMUM,
  DELIVERY_BROKEN_SHARE,
  DELIVERY_STRUGGLING_SHARE,
  readRecordingDelivery,
  type DeliveryCounts,
  type RecordingDelivery,
} from "./recording-delivery";
export {
  coveredTogether,
  inRecordedOrder,
  momentWithinSession,
  type RecordedSpan,
} from "./recording-pieces";
export { readPushDelivery, PUSH_DELIVERY_MINIMUM, type PushDelivery, type PushDeliveryCounts } from "./push-delivery";

export { readRuntimeReadiness, RUNTIME_READINESS_MINIMUM, type RuntimeReadiness, type RuntimeReadinessCounts } from "./runtime-readiness";

export { readRelationalRead, RELATIONAL_SLOW_MS, RELATIONAL_READ_MINIMUM, type RelationalRead, type RelationalReadCounts } from "./relational-read";

export { readEmailDelivery, EMAIL_DELIVERY_MINIMUM, type EmailDelivery, type EmailDeliveryCounts } from "./email-delivery";

export * from "./background-work";

export * from "./learning-ai";

export * from "./presenter-processing";

export * from "./wav-audio";

export * from "./spoken-words";
export * from "./recordings-she-may-hear";

export {
  minuteOfDayUtc,
  whyWakeThisMinute,
} from "./waking-phones";
export type { WakeQuestion, WakeReason } from "./waking-phones";

export {
  isHeadlineQuestion,
  surveySessionBreakdown,
  type SurveySessionBreakdown,
  type SurveySessionChoiceTally,
  type SurveySessionQuestionTally,
  type SurveySessionRef,
  type SurveySessionResponse,
  type SurveySessionTally,
} from "./survey-session-breakdown";

export {
  surveyCrossReading,
  type SurveyActivity,
  type SurveyCohortMember,
  type SurveyCrossReading,
  type SurveyFinding,
  type SurveyRatingBehavior,
  type SurveyReach,
  type SurveySessionActivity,
  type SurveySlotReading,
} from "./survey-cross-reading";
